/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router'
import { filter, firstValueFrom, lastValueFrom } from 'rxjs'
import { take } from 'rxjs/operators'
import { ConfigService } from '../configuration/config.service'
import { LocaleHandlingService } from '../locale-handling/locale-handling.service'
import { LocaleRetrieveUrlParamsService } from '../configuration/locale-retrieve-url-params-signup.service'
import { CookieConsentImplementationService } from '../cookie-consent/cookie-consent.service'
import { OAuthGuardService } from '../oauth/oauth-guard.service'
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service'

@Injectable({
  providedIn: 'root',
})
export class SetupGuardService {
  private configService = inject(ConfigService)
  private localeHandlingService = inject(LocaleHandlingService)
  private localeRetrieveUrlParamsService = inject(LocaleRetrieveUrlParamsService)
  private router = inject(Router)
  private cookieConsentBackendService = inject(CookieConsentImplementationService)
  private authService = inject(OAuthGuardService)
  private featureToggleService = inject(FeatureToggleService)

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const params = this.localeRetrieveUrlParamsService.retrieve(route)
    this.configService.setUrlLocaleCountryInformation(params.locale, params.country)
    if (!(await firstValueFrom(this.authService.canActivate(route, state)))) {
      return false
    }
    await lastValueFrom(this.configService.init(params.locale, params.country))
    const localeHandlingSuccess = await this.localeHandlingService.setup()
    this.featureToggleService.initialize(route.fragment ?? '')

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(async () => {
        await this.cookieConsentBackendService.init(params.country)
      })

    return localeHandlingSuccess
  }
}
